import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  BsBuildingsFill,
  BsAwardFill,
  BsShieldCheck,
  BsTruck,
  BsCheckCircleFill,
  BsTelephoneForwardFill,
  BsBoxSeamFill,
  BsFileEarmarkSpreadsheetFill,
} from "react-icons/bs";

const SYSTEM_TOKENS = {
  deepBlue: "#1a3c6b",
  mintCyan: "#36bfa8",
  cleanGlass: "rgba(255, 255, 255, 0.75)",
  borderToken: "rgba(226, 232, 240, 0.8)",
};

const HorecaPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "HORECA Institutional Partnerships | NGO Waters";
  }, []);

  // Track state for the interactive pricing catalog without resetting mapping elements
  const [selectedCatalog, setSelectedCatalog] = useState("hotels");

  const catalogMatrix = {
    hotels: [
      {
        SKU: "250ml Hospitality Pods",
        operationalUse: "Conference Rooms & Banquet Buffets",
        pricing: "Custom Institutional Rate",
      },
      {
        SKU: "500ml Retail Shield",
        operationalUse: "In-Room Complimentary Hydration",
        pricing: "Contract Pricing",
      },
      {
        SKU: "1L Standard Premium",
        operationalUse: "Fine Dining Tables & Gym Units",
        pricing: "Volume Wholesale",
      },
    ],
    cafes: [
      {
        SKU: "500ml Clean Line",
        operationalUse: "Quick-Service Bistro Counter Sales",
        pricing: "Direct Distributor Rate",
      },
      {
        SKU: "20L Bulk Transporter",
        operationalUse: "Espresso Machines & Kitchen Water Processing",
        pricing: "Flat Monthly Slab",
      },
    ],
    catering: [
      {
        SKU: "250ml Eco Pods",
        operationalUse: "High-Volume Wedding & Event Setups",
        pricing: "Batch Order Matrix",
      },
      {
        SKU: "2L Institutional Bottles",
        operationalUse: "Long-Duration Outdoor Buffets",
        pricing: "Pallet Level Pricing",
      },
    ],
  };

  return (
    <div
      className="w-100 py-5 px-3 px-lg-5 position-relative"
      style={{
        fontFamily: "'Inter', sans-serif",
        background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
        minHeight: "100vh",
      }}
    >
      {/* Structural Fluid Backdrop Mesh */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "550px",
          height: "550px",
          background: "rgba(26, 60, 107, 0.03)",
          top: "-50px",
          right: "-5%",
          filter: "blur(120px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(54, 191, 216, 0.03)",
          bottom: "5%",
          left: "-5%",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      {/* Premium Corporate Section Header */}
      <header
        className="mx-auto text-center mb-5 position-relative z-1"
        style={{ maxWidth: "800px" }}
      >
        <div className="d-inline-flex align-items-center gap-2 px-3 py-1.5 mb-3 bg-white border rounded-pill shadow-xs">
          <BsBuildingsFill className="text-primary" size={13} />
          <span
            className="fw-semibold text-muted tracking-wide text-uppercase"
            style={{ fontSize: "0.7rem", letterSpacing: "0.5px" }}
          >
            Commercial Division Supply Layer
          </span>
        </div>
        <h1
          className="fw-extrabold text-dark tracking-tight mb-2"
          style={{ fontSize: "calc(1.8rem + 1.2vw)", letterSpacing: "-1.5px" }}
        >
          HORECA Procurement Framework
        </h1>
        <p
          className="text-secondary small mx-auto mb-0"
          style={{ maxWidth: "600px", lineHeight: "1.6" }}
        >
          Bulk mineral hydration pipelines engineered specifically for Hotels,
          Restaurants, and Luxury Event Caterers operating within the
          Mathura-Vrindavan economic block.
        </p>
      </header>

      {/* Flat Dynamic Info Architecture Grid */}
      <main
        className="mx-auto position-relative z-1 mb-5"
        style={{ maxWidth: "1200px" }}
      >
        <div className="row g-4 match-height">
          {/* Card 1: Core Institutional Pillar Strategy */}
          <motion.div
            className="col-lg-4 col-md-6"
            initial={{ y: 15, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="h-100 p-4 border"
              style={{
                borderRadius: "20px",
                backgroundColor: SYSTEM_TOKENS.cleanGlass,
                backdropFilter: "blur(16px)",
                borderColor: SYSTEM_TOKENS.borderToken,
              }}
            >
              <div className="d-flex align-items-center gap-2.5 mb-3 pb-2 border-bottom">
                <div className="p-2 bg-light text-primary rounded-3 d-flex align-items-center justify-content-center">
                  <BsAwardFill size={16} />
                </div>
                <h5 className="m-0 fw-bold text-dark h6">
                  SLA Quality Assurance
                </h5>
              </div>
              <p
                className="text-secondary small mb-3"
                style={{ lineHeight: "1.6" }}
              >
                Every batch dispatched under{" "}
                <strong>PONIA FOOD AND BEVERAGE PRIVATE LIMITED</strong> passes
                through real-time mineral analysis and automated monitoring
                grids.
              </p>
              <ul className="list-unstyled p-0 m-0 d-flex flex-column gap-2">
                {[
                  "Rigorous UV & Multi-Layer RO Filtration",
                  "Strict BIS (Bureau of Indian Standards) Compliance",
                  "Certified FSSAI Processing Standards",
                ].map((text, idx) => (
                  <li
                    key={idx}
                    className="d-flex align-items-start gap-2 small text-muted"
                  >
                    <BsShieldCheck
                      className="text-success mt-0.5 flex-shrink-0"
                      size={14}
                    />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Card 2: Micro Logistics Routing Channel */}
          <motion.div
            className="col-lg-4 col-md-6"
            initial={{ y: 15, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.05 }}
          >
            <div
              className="h-100 p-4 border"
              style={{
                borderRadius: "20px",
                backgroundColor: SYSTEM_TOKENS.cleanGlass,
                backdropFilter: "blur(16px)",
                borderColor: SYSTEM_TOKENS.borderToken,
              }}
            >
              <div className="d-flex align-items-center gap-2.5 mb-3 pb-2 border-bottom">
                <div className="p-2 bg-light text-success rounded-3 d-flex align-items-center justify-content-center">
                  <BsTruck size={16} />
                </div>
                <h5 className="m-0 fw-bold text-dark h6">
                  Hyperlocal Routing Engine
                </h5>
              </div>
              <p
                className="text-secondary small mb-3"
                style={{ lineHeight: "1.6" }}
              >
                Our optimized fleet structure provides direct-to-hub dispatch
                operations across local corridors, bypassing external channel
                distributions to preserve margin benefits.
              </p>
              <div className="p-3 bg-white rounded-3 border">
                <div className="fw-bold text-dark small mb-1">
                  Direct Route Execution Matrix:
                </div>
                <div
                  className="text-muted small"
                  style={{ fontSize: "0.78rem" }}
                >
                  Mathura Center • Vrindavan Sector Blocks • Interconnected
                  Highway Hospitality Hubs
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card 3: Interactive Catalog Grid Showcase */}
          <motion.div
            className="col-lg-4 col-md-12"
            initial={{ y: 15, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            <div
              className="h-100 p-4 border bg-white shadow-sm"
              style={{ borderRadius: "20px" }}
            >
              <div className="d-flex align-items-center gap-2.5 mb-3 pb-2 border-bottom">
                <div className="p-2 bg-light text-info rounded-3 d-flex align-items-center justify-content-center">
                  <BsBoxSeamFill size={16} />
                </div>
                <h5 className="m-0 fw-bold text-dark h6">
                  SKU Matrix Selection
                </h5>
              </div>

              {/* Dynamic Flat Switch Pills */}
              <div
                className="d-flex bg-light p-1 rounded-3 mb-3 border"
                style={{ gap: "2px" }}
              >
                {Object.keys(catalogMatrix).map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCatalog(category)}
                    className="btn btn-sm text-capitalize w-100 py-1.5 border-0 font-weight-semibold"
                    style={{
                      fontSize: "0.78rem",
                      borderRadius: "6px",
                      backgroundColor:
                        selectedCatalog === category ? "#fff" : "transparent",
                      color:
                        selectedCatalog === category
                          ? SYSTEM_TOKENS.deepBlue
                          : "#64748b",
                      boxShadow:
                        selectedCatalog === category
                          ? "0 2px 6px rgba(0,0,0,0.04)"
                          : "none",
                      transition: "all 0.15s ease",
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>

              {/* Catalog Manifest Output Table */}
              <div className="table-responsive m-0">
                <table
                  className="table table-borderless table-sm m-0 align-middle"
                  style={{ fontSize: "0.78rem" }}
                >
                  <thead>
                    <tr
                      className="border-bottom text-muted text-uppercase font-monospace"
                      style={{ fontSize: "0.65rem" }}
                    >
                      <th className="py-2">Pack Spec</th>
                      <th className="py-2 text-end">SLA Pricing Matrix</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AnimatePresence mode="wait">
                      {catalogMatrix[selectedCatalog].map((item, index) => (
                        <tr key={index} className="border-bottom border-light">
                          <td className="py-2">
                            <div className="fw-bold text-dark">{item.SKU}</div>
                            <div
                              className="text-muted ultra-small"
                              style={{ fontSize: "0.72rem" }}
                            >
                              {item.operationalUse}
                            </div>
                          </td>
                          <td className="py-2 text-primary fw-bold text-end align-middle">
                            {item.pricing}
                          </td>
                        </tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Enterprise SLA Onboarding Trigger Section */}
      <section
        className="mx-auto text-center position-relative z-1 pt-3"
        style={{ maxWidth: "700px" }}
      >
        <motion.div
          className="p-4 bg-white border d-flex flex-column flex-sm-row align-items-center justify-content-between text-sm-start gap-3 shadow-sm"
          style={{ borderRadius: "16px" }}
          whileHover={{ y: -2 }}
          transition={{ duration: 0.2 }}
        >
          <div>
            <h6 className="fw-bold text-dark mb-0.5">
              Initiate Continuous Contract Logistics
            </h6>
            <p className="text-muted small m-0" style={{ fontSize: "0.82rem" }}>
              Route your volume allocation request straight to our dispatch
              supervisors.
            </p>
          </div>
          <a
            href="/contact"
            className="btn text-white px-4 py-2 fw-semibold border-0 d-flex align-items-center gap-2 flex-shrink-0"
            style={{
              backgroundColor: SYSTEM_TOKENS.deepBlue,
              borderRadius: "8px",
              fontSize: "0.85rem",
            }}
          >
            <BsTelephoneForwardFill size={13} /> Secure Supply Line
          </a>
        </motion.div>
      </section>

      {/* Statutory Regulatory Registration Base Footer Block */}
      <footer
        className="w-100 mt-5 pt-4 text-center border-top text-muted position-relative z-1"
        style={{
          fontSize: "0.7rem",
          letterSpacing: "0.3px",
          borderColor: "rgba(0,0,0,0.05)",
        }}
      >
        <div className="fw-semibold text-dark mb-1">
          PONIA FOOD AND BEVERAGE PRIVATE LIMITED
        </div>
        <div>
          CIN: U11043UP2024PTC213517 • ENTERPRISE PROCUREMENTS SYSTEM DATA LAYER
        </div>
        <div
          className="mt-2 text-uppercase font-monospace text-muted"
          style={{ fontSize: "0.65rem", opacity: 0.6 }}
        >
          UI Configuration Layer Powered Safely via System Node: KEYTRON
        </div>
      </footer>
    </div>
  );
};

export default HorecaPage;
